import React from "react";
import { Check } from "lucide-react";
import { WHO_WE_ARE, HOW_IT_WORKS, HOW_IT_WORKS_HEADING, TREATMENT_IMAGES, OFFER, OFFER_INCLUDES, BUSINESS } from "../mock";
import { Reveal, SectionEyebrow, CtaButton } from "./common";

export const WhoWeAre = () => (
  <section className="bg-brand-ivory py-20">
    <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 px-5 md:px-8 lg:grid-cols-2">
      <Reveal>
        <div className="overflow-hidden rounded-3xl shadow-[0_30px_70px_-35px_rgba(35,35,32,0.5)]">
          <img src={WHO_WE_ARE.image} alt={`The ${BUSINESS.name} team`} className="h-[440px] w-full object-cover" />
        </div>
      </Reveal>
      <Reveal delay={100}>
        <SectionEyebrow>{BUSINESS.name}</SectionEyebrow>
        <h2 className="mt-4 font-serif text-3xl text-brand-ink md:text-4xl">{WHO_WE_ARE.title}</h2>
        <p className="mt-2 font-display text-lg italic text-brand-golddark">{WHO_WE_ARE.subtitle}</p>
        <p className="mt-4 font-sans text-[15px] leading-relaxed text-brand-muted">{WHO_WE_ARE.text}</p>
        <ul className="mt-6 space-y-3">
          {WHO_WE_ARE.bullets.map((b) => (
            <li key={b} className="flex items-start gap-3">
              <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-brand-sage">
                <Check size={13} className="text-white" />
              </span>
              <span className="font-sans text-[15px] text-brand-ink">{b}</span>
            </li>
          ))}
        </ul>
        <CtaButton className="mt-8" toForm>Claim My Spot</CtaButton>
      </Reveal>
    </div>
  </section>
);

export const HowItWorks = () => (
  <section className="bg-brand-cream/50 py-20">
    <div className="mx-auto max-w-6xl px-5 md:px-8">
      <Reveal className="text-center">
        <SectionEyebrow center>How It Works</SectionEyebrow>
        <h2 className="mt-4 font-serif text-3xl text-brand-ink md:text-4xl">{HOW_IT_WORKS_HEADING}</h2>
        <p className="mx-auto mt-3 max-w-xl font-sans text-sm text-brand-muted">
          Your first {OFFER.durationLabel} visit — here's how it goes.
        </p>
      </Reveal>
      <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
        {HOW_IT_WORKS.map((s, i) => (
          <Reveal key={s.step} delay={i * 100}>
            <div className="h-full rounded-3xl bg-white p-8 shadow-[0_18px_50px_-30px_rgba(35,35,32,0.45)] ring-1 ring-brand-cream">
              <span className="font-serif text-5xl font-semibold text-brand-gold/40">{s.step}</span>
              <h3 className="mt-4 font-serif text-xl text-brand-ink">{s.title}</h3>
              <p className="mt-3 font-sans text-sm leading-relaxed text-brand-muted">{s.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <div className="mt-10 text-center">
        <CtaButton toForm>Claim My Spot</CtaButton>
      </div>
    </div>
  </section>
);

export const TheTreatment = () => (
  <section className="bg-brand-ivory py-20">
    <div className="mx-auto max-w-6xl px-5 md:px-8">
      <Reveal className="text-center">
        <SectionEyebrow center>The Treatment</SectionEyebrow>
        <h2 className="mt-4 font-serif text-3xl text-brand-ink md:text-4xl">Inside the room</h2>
      </Reveal>
      <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
        {TREATMENT_IMAGES.map((src, i) => (
          <Reveal key={i} delay={i * 90}>
            <div className="group overflow-hidden rounded-2xl shadow-[0_18px_50px_-28px_rgba(35,35,32,0.5)]">
              <img src={src} alt={`Treatment at ${BUSINESS.name}`} className="aspect-[3/4] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const OfferCard = () => (
  <section className="bg-brand-cream/50 py-20">
    <div className="mx-auto max-w-2xl px-5 md:px-8">
      <Reveal>
        <div className="relative overflow-hidden rounded-3xl bg-white p-8 text-center shadow-[0_30px_80px_-35px_rgba(35,35,32,0.5)] ring-1 ring-brand-cream md:p-12">
          <span className="inline-block rounded-full bg-brand-sage/10 px-4 py-1.5 font-sans text-xs font-semibold uppercase tracking-[0.18em] text-brand-sage">
            Limited Intro Offer
          </span>
          <h2 className="mt-5 font-serif text-3xl text-brand-ink md:text-4xl">{OFFER.name}</h2>
          <p className="mt-2 font-sans text-sm text-brand-muted">Here's everything you get on your first visit</p>
          <div className="mt-6 flex items-end justify-center gap-3">
            <span className="font-serif text-2xl text-brand-muted line-through">${OFFER.regPrice}</span>
            <span className="font-serif text-6xl font-semibold text-brand-sage">${OFFER.price}</span>
          </div>
          <p className="mt-1 font-sans text-xs uppercase tracking-[0.16em] text-brand-muted">
            {OFFER.currency} · {OFFER.duration} · {OFFER.includedCount} included items
          </p>
          <ul className="mx-auto mt-7 max-w-md space-y-3 text-left">
            {OFFER_INCLUDES.map((b) => (
              <li key={b} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-brand-sage">
                  <Check size={13} className="text-white" />
                </span>
                <span className="font-sans text-[15px] text-brand-ink">{b}</span>
              </li>
            ))}
          </ul>
          <CtaButton className="mt-9 w-full" toForm>Claim My Spot</CtaButton>
        </div>
      </Reveal>
    </div>
  </section>
);
